import {
  FIRST_RUN_UP_DURATION_MS,
  FLIGHT_DURATION_MS,
  laneTargets,
  REPEAT_RUN_UP_DURATION_MS,
  RESULT_DURATION_MS,
  shotBandThresholds,
  TOTAL_SHOTS,
} from './constants';
import { clamp, createShotSeed, deterministicRoll, easeInOutSine, easeOutBack, easeOutCubic, getChargePower, getPowerValue, lerp, lerpVec3 } from './helpers';
import { getCurrentShotNumber, getRunSummary } from './session';
import { EngineState, UiState } from './types';
import { resolveShot, spawnOutcomeParticles, updateParticles } from './gameplay';

const BALL_RELEASE_DELAY_MS = 96;
const KICK_RECOVERY_MS = 280;
const KEEPER_REACT_DELAY_MS = 48;

export const getSceneSnapshot = (engine: EngineState, nowMs: number) => {
  const phaseElapsedMs = Math.max(0, nowMs - engine.phaseStartedAtMs);
  const power = getPowerValue(engine.phase, nowMs, engine.shot, engine.chargeStartedAtMs);
  const runUpDurationMs = engine.stats.attempts === 0 ? FIRST_RUN_UP_DURATION_MS : REPEAT_RUN_UP_DURATION_MS;
  const dribbleEase = easeInOutSine(clamp(phaseElapsedMs / runUpDurationMs, 0, 1));
  const flightProgress = engine.phase === 'flight' ? clamp(phaseElapsedMs / FLIGHT_DURATION_MS, 0, 1) : engine.phase === 'result' ? 1 : 0;
  const ballFlightProgress =
    engine.phase === 'flight'
      ? clamp(Math.max(phaseElapsedMs - BALL_RELEASE_DELAY_MS, 0) / (FLIGHT_DURATION_MS - BALL_RELEASE_DELAY_MS), 0, 1)
      : engine.phase === 'result'
        ? 1
        : 0;
  const celebrationProgress =
    engine.phase === 'result'
      ? clamp(phaseElapsedMs / RESULT_DURATION_MS, 0, 1)
      : engine.phase === 'summary'
        ? 1
        : 0;
  const kickPoseProgress = easeOutCubic(clamp(phaseElapsedMs / KICK_RECOVERY_MS, 0, 1));
  const kickPose = { x: -0.44, y: 0, z: 12.06 };

  let attacker =
    engine.phase === 'dribble'
      ? { x: lerp(-1.48, -0.36, dribbleEase), y: 0, z: lerp(6.6, 11.85, dribbleEase) }
      : { x: -0.36, y: 0, z: 11.85 };

  if (engine.phase === 'flight' && engine.shot) {
    attacker = {
      x: lerp(-0.36, kickPose.x, kickPoseProgress),
      y: Math.sin(clamp(phaseElapsedMs / KICK_RECOVERY_MS, 0, 1) * Math.PI) * 0.038,
      z: lerp(11.85, kickPose.z, kickPoseProgress),
    };
  }

  if (engine.phase === 'result' && engine.shot) {
    attacker = { x: lerp(kickPose.x, -0.5, celebrationProgress), y: 0, z: lerp(kickPose.z, 12.25, celebrationProgress) };
  }

  const shot = engine.shot;
  const keeperBase = { x: 0, y: 0, z: 18.55 };
  const keeperReachScale =
    shot?.keeperAction === 'save'
      ? 0.52
      : shot?.keeperAction === 'wrong-side'
        ? 0.36
        : shot?.keeperAction === 'late'
          ? 0.06
          : 0;
  const keeperLiftScale =
    shot?.keeperAction === 'save'
      ? 0.34
      : shot?.keeperAction === 'wrong-side'
        ? 0.16
        : shot?.keeperAction === 'late'
          ? 0.04
          : 0;
  const keeperReactDelayMs =
    shot?.keeperAction === 'save'
      ? KEEPER_REACT_DELAY_MS
      : shot?.keeperAction === 'wrong-side'
        ? 128
        : shot?.keeperAction === 'late'
          ? 220
          : FLIGHT_DURATION_MS;
  const keeperDive =
    shot && engine.phase === 'flight'
      ? clamp(
          (Math.max(phaseElapsedMs - keeperReactDelayMs, 0) / Math.max(1, FLIGHT_DURATION_MS - keeperReactDelayMs)) *
            (shot.keeperAction === 'save' ? 1.08 : shot.keeperAction === 'wrong-side' ? 0.82 : 0.28),
          0,
          1,
        )
      : shot && engine.phase === 'result'
        ? shot.keeperAction === 'hold'
          ? 0
          : shot.keeperAction === 'late'
            ? 0.28
            : 1
        : 0;
  const keeper = shot
    ? {
        x: lerp(keeperBase.x, laneTargets[shot.keeperLane].x * keeperReachScale, easeOutBack(keeperDive)),
        y: lerp(keeperBase.y, Math.max(0.16, shot.target.y * keeperLiftScale), easeOutCubic(keeperDive)),
        z: keeperBase.z,
      }
    : keeperBase;

  const shotStart = { x: -0.03, y: 0.28, z: 12.42 };
  let ball = shotStart;
  if (engine.phase === 'idle') {
    ball = { x: attacker.x + 0.22, y: 0.22, z: attacker.z + 0.26 };
  } else if (engine.phase === 'dribble') {
    const dribbleBeat = Math.sin(phaseElapsedMs / 78);
    ball = {
      x: attacker.x + 0.24 + dribbleBeat * 0.03,
      y: 0.19 + Math.abs(dribbleBeat) * 0.11,
      z: attacker.z + 0.14 + Math.cos(phaseElapsedMs / 92) * 0.05,
    };
  } else if (engine.phase === 'aim') {
    ball = { x: shotStart.x, y: 0.2 + Math.abs(Math.sin(phaseElapsedMs / 116)) * 0.07, z: shotStart.z };
  } else if (shot) {
    if (shot.outcome === 'save') {
      const reboundSeed = createShotSeed(shot.id, shot.power);
      const centerReboundDrift = deterministicRoll(reboundSeed, 400) < 0.5 ? -0.78 : 0.78;
      const interceptPoint = lerpVec3(shotStart, shot.target, shot.saveProgress);
      const interceptHeight = Math.sin(shot.saveProgress * Math.PI) * 1.6;
      if (ballFlightProgress <= shot.saveProgress) {
        const localProgress = ballFlightProgress / shot.saveProgress;
        ball = lerpVec3(shotStart, interceptPoint, localProgress);
        ball.y += Math.sin(localProgress * Math.PI) * interceptHeight;
      } else {
        const rebound = clamp((ballFlightProgress - shot.saveProgress) / (1 - shot.saveProgress), 0, 1);
        ball = {
          x: interceptPoint.x + (shot.lane === 'left' ? 1 : shot.lane === 'right' ? -1 : centerReboundDrift) * rebound,
          y: Math.max(0.26, interceptPoint.y + interceptHeight * 0.2 - 0.9 * rebound),
          z: interceptPoint.z - 1.1 * rebound,
        };
      }
    } else {
      ball = lerpVec3(shotStart, shot.target, ballFlightProgress);
      ball.y += Math.sin(ballFlightProgress * Math.PI) * (shot.perfect ? 2.15 : shot.outcome === 'goal' ? 1.65 : 1.3);
    }
  }

  return { phaseElapsedMs, power, attacker, keeper, ball, flightProgress, celebrationProgress };
};

export const startAttack = (engine: EngineState, nowMs: number) => {
  engine.phase = 'dribble';
  engine.phaseStartedAtMs = nowMs;
  engine.shot = null;
  engine.appliedResultShotId = null;
  engine.particles = [];
  engine.chargeStartedAtMs = null;
};

export const fireShot = (engine: EngineState, nowMs: number, lockedPower?: number) => {
  if (engine.phase !== 'aim') {
    return false;
  }

  const power = lockedPower ?? getPowerValue('aim', nowMs, engine.shot, engine.chargeStartedAtMs);
  engine.stats = { ...engine.stats, attempts: engine.stats.attempts + 1 };
  engine.shot = resolveShot(engine.aim, power, engine.nextShotId);
  engine.nextShotId += 1;
  engine.chargeStartedAtMs = null;
  engine.phase = 'flight';
  engine.phaseStartedAtMs = nowMs;
  return true;
};

export const startCharge = (engine: EngineState, nowMs: number) => {
  if (engine.phase !== 'aim' || engine.chargeStartedAtMs !== null) {
    return false;
  }

  engine.chargeStartedAtMs = nowMs;
  return true;
};

export const cancelCharge = (engine: EngineState) => {
  if (engine.chargeStartedAtMs === null) {
    return false;
  }

  engine.chargeStartedAtMs = null;
  return true;
};

export const releaseCharge = (engine: EngineState, nowMs: number) => {
  if (engine.phase !== 'aim' || engine.chargeStartedAtMs === null) {
    return false;
  }

  const lockedPower = getChargePower(nowMs - engine.chargeStartedAtMs);
  return fireShot(engine, nowMs, lockedPower);
};

const applyShotResultIfNeeded = (engine: EngineState) => {
  if (!engine.shot || engine.appliedResultShotId === engine.shot.id) {
    return;
  }

  const streak = engine.shot.outcome === 'goal' ? engine.stats.streak + 1 : 0;
  engine.stats = {
    ...engine.stats,
    goals: engine.stats.goals + (engine.shot.outcome === 'goal' ? 1 : 0),
    saves: engine.stats.saves + (engine.shot.outcome === 'save' ? 1 : 0),
    misses: engine.stats.misses + (engine.shot.outcome === 'miss' ? 1 : 0),
    streak,
    bestStreak: Math.max(engine.stats.bestStreak, streak),
    perfectGoals: engine.stats.perfectGoals + (engine.shot.perfect && engine.shot.outcome === 'goal' ? 1 : 0),
  };
  engine.appliedResultShotId = engine.shot.id;
  spawnOutcomeParticles(engine, engine.shot);
};

export const advanceEngine = (engine: EngineState, nowMs: number, deltaMs: number) => {
  updateParticles(engine, deltaMs);
  const phaseElapsedMs = nowMs - engine.phaseStartedAtMs;

  const runUpDurationMs = engine.stats.attempts === 0 ? FIRST_RUN_UP_DURATION_MS : REPEAT_RUN_UP_DURATION_MS;
  if (engine.phase === 'dribble' && phaseElapsedMs >= runUpDurationMs) {
    engine.phase = 'aim';
    engine.phaseStartedAtMs = nowMs;
    engine.chargeStartedAtMs = null;
    return;
  }

  if (engine.phase === 'flight' && phaseElapsedMs >= FLIGHT_DURATION_MS) {
    engine.phase = 'result';
    engine.phaseStartedAtMs = nowMs;
    engine.chargeStartedAtMs = null;
    applyShotResultIfNeeded(engine);
    return;
  }

  if (engine.phase === 'result' && phaseElapsedMs >= RESULT_DURATION_MS) {
    if (engine.stats.attempts >= TOTAL_SHOTS) {
      engine.phase = 'summary';
      engine.phaseStartedAtMs = nowMs;
      engine.chargeStartedAtMs = null;
      engine.particles = [];
    } else {
      startAttack(engine, nowMs);
    }
  }
};

export const buildUiState = (engine: EngineState, nowMs: number, variant: 'home' | 'sport'): UiState => {
  const { power } = getSceneSnapshot(engine, nowMs);
  const chargeActive = engine.chargeStartedAtMs !== null;
  const shared = {
    phase: engine.phase,
    aim: engine.aim,
    power,
    shot: engine.shot,
    stats: { ...engine.stats },
    runSummary: getRunSummary(engine.stats),
    shotNumber: getCurrentShotNumber(engine.stats),
    totalShots: TOTAL_SHOTS,
    chargeActive,
  };

  if (engine.phase === 'dribble') {
    return {
      ...shared,
      statusTitle: engine.stats.attempts === 0 ? 'First run-up' : `Shot ${engine.stats.attempts + 1}`,
      statusBody: 'Aim across the goal, hold to charge, then release inside the strike window.',
      toneClassName: 'text-white',
      actionEnabled: false,
    };
  }

  if (engine.phase === 'aim') {
    return {
      ...shared,
      statusTitle: chargeActive ? 'Release to shoot' : 'Hold to charge',
      statusBody: 'Move across the goal to aim. The marked strike window guarantees a perfect goal.',
      toneClassName: 'text-white',
      actionEnabled: true,
    };
  }

  if (engine.phase === 'flight') {
    return {
      ...shared,
      statusTitle: 'Shot away',
      statusBody: 'The shot is resolving now.',
      toneClassName: 'text-white',
      actionEnabled: false,
    };
  }

  if (engine.phase === 'result' && engine.shot) {
    const overhitMiss = engine.shot.outcome === 'miss' && engine.shot.power >= shotBandThresholds.redHighMin;
    return {
      ...shared,
      statusTitle:
        engine.shot.outcome === 'goal'
          ? engine.shot.perfect
            ? 'Perfect goal'
            : 'Goal'
          : engine.shot.outcome === 'save'
            ? 'Saved'
            : overhitMiss
              ? 'Over the bar'
              : 'Pulled wide',
      statusBody:
        engine.shot.outcome === 'goal'
          ? engine.shot.perfect
            ? 'Perfect timing. The strike window delivered.'
            : 'Goal. Good placement beat the keeper.'
          : engine.shot.outcome === 'save'
            ? 'Saved. Try a different lane or sharper timing.'
            : overhitMiss
              ? 'Too much power sent it over the bar.'
              : 'Wide. Bring the aim back toward the goal.',
      toneClassName:
        engine.shot.outcome === 'goal'
          ? engine.shot.perfect
            ? 'text-emerald-300'
            : 'text-sky-200'
          : engine.shot.outcome === 'save'
            ? 'text-sky-300'
            : 'text-amber-300',
      actionEnabled: false,
    };
  }

  if (engine.phase === 'summary') {
    return {
      ...shared,
      statusTitle: `Run complete · ${shared.runSummary.grade}`,
      statusBody: `${shared.runSummary.goals} goals, ${shared.runSummary.perfectGoals} perfect, ${shared.runSummary.score} points.`,
      toneClassName: 'text-emerald-200',
      actionEnabled: false,
    };
  }

  return {
    ...shared,
    statusTitle: 'Ready',
    statusBody: 'The first run starts when the game enters view.',
    toneClassName: 'text-white',
    actionEnabled: false,
  };
};
